/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { slackDirectoryConnectionsSetMapping } from "../funcs/slackDirectoryConnectionsSetMapping.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SlackDirectoryMember } from "../models/components/slackdirectorymember.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetSlackIdentityMappingRequest,
  SetSlackIdentityMappingSecurity,
} from "../models/operations/setslackidentitymapping.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetSlackIdentityMappingMutationVariables = {
  request: SetSlackIdentityMappingRequest;
  security?: SetSlackIdentityMappingSecurity | undefined;
  options?: RequestOptions;
};

export type SetSlackIdentityMappingMutationData = SlackDirectoryMember;

export type SetSlackIdentityMappingMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setMapping slackDirectoryConnections
 *
 * @remarks
 * Explicitly confirm, reassign or remove a Slack association. This grants no permissions and does not establish runtime eligibility.
 */
export function useSetSlackIdentityMappingMutation(
  options?: MutationHookOptions<
    SetSlackIdentityMappingMutationData,
    SetSlackIdentityMappingMutationError,
    SetSlackIdentityMappingMutationVariables
  >,
): UseMutationResult<
  SetSlackIdentityMappingMutationData,
  SetSlackIdentityMappingMutationError,
  SetSlackIdentityMappingMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetSlackIdentityMappingMutation(client, options),
    ...options,
  });
}

export function mutationKeySetSlackIdentityMapping(): MutationKey {
  return ["@gram/client", "slackDirectoryConnections", "setMapping"];
}

export function buildSetSlackIdentityMappingMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetSlackIdentityMappingMutationVariables,
  ) => Promise<SetSlackIdentityMappingMutationData>;
} {
  return {
    mutationKey: mutationKeySetSlackIdentityMapping(),
    mutationFn: function setSlackIdentityMappingMutationFn({
      request,
      security,
      options,
    }): Promise<SetSlackIdentityMappingMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(slackDirectoryConnectionsSetMapping(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
